import { TriageTag, PatientStatus, Patient, getTimer } from './gameData';
import { getTimers } from './gameDataOverrides';

export interface CustomPatientCase {
  id: string;
  name: string;
  age: number;
  mechanism: string;
  vitals: {
    hr: number;
    bp: string;
    rr: number;
    spo2: number;
    gcs: number;
  };
  symptoms: string[];
  correctTag: TriageTag;
  timerDuration: number;
  initialStatus: PatientStatus;
}

const STORAGE_KEY = 'mci-triage-custom-cases';

export function loadCustomCases(): CustomPatientCase[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveCustomCases(cases: CustomPatientCase[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cases));
}

export function addCustomCase(c: Omit<CustomPatientCase, 'id'>): CustomPatientCase {
  const cases = loadCustomCases();
  const newCase: CustomPatientCase = { ...c, id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
  cases.push(newCase);
  saveCustomCases(cases);
  return newCase;
}

export function updateCustomCase(id: string, updates: Partial<Omit<CustomPatientCase, 'id'>>): void {
  const cases = loadCustomCases();
  const idx = cases.findIndex(c => c.id === id);
  if (idx >= 0) {
    cases[idx] = { ...cases[idx], ...updates };
    saveCustomCases(cases);
  }
}

export function deleteCustomCase(id: string): void {
  saveCustomCases(loadCustomCases().filter(c => c.id !== id));
}

export function customCaseToPatient(c: CustomPatientCase, wave: number = 1): Patient {
  const baseTimers = getTimers();
  const timerDuration = getTimer(c.correctTag, wave, baseTimers);
  // Derive START fields from correctTag
  const isGreen = c.correctTag === 'green';
  const isBlack = c.correctTag === 'black';
  const isRed = c.correctTag === 'red';
  return {
    id: `patient-custom-${c.id}-${Date.now()}`,
    name: c.name,
    age: c.age,
    mechanism: c.mechanism,
    vitals: { ...c.vitals },
    symptoms: [...c.symptoms],
    correctTag: c.correctTag,
    timerDuration,
    timeRemaining: timerDuration,
    status: c.initialStatus,
    deteriorated: false,
    triaged: false,
    canWalk: isGreen,
    isBreathing: !isBlack,
    breathesAfterReposition: isBlack ? false : true,
    hasRadialPulse: !(isRed && c.vitals.hr > 120),
    capRefill: isRed ? 4 : isBlack ? 0 : 2,
    followsCommands: !isBlack && !(isRed && c.vitals.gcs < 10),
    isPediatric: false,
    avpuScore: 'A',
    respondsToRescueBreaths: false,
    rrAfterRescueBreaths: 0,
  };
}

export function exportCasesJSON(cases: CustomPatientCase[]): string {
  return JSON.stringify(cases, null, 2);
}

export function importCasesJSON(json: string): CustomPatientCase[] {
  const parsed = JSON.parse(json);
  if (!Array.isArray(parsed)) throw new Error('Invalid format: expected an array');
  return parsed.map((c: any) => ({
    id: c.id || `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: String(c.name || 'Unknown'),
    age: Number(c.age) || 30,
    mechanism: String(c.mechanism || 'Unknown'),
    vitals: {
      hr: Number(c.vitals?.hr) || 80,
      bp: String(c.vitals?.bp || '120/80'),
      rr: Number(c.vitals?.rr) || 16,
      spo2: Number(c.vitals?.spo2) || 98,
      gcs: Number(c.vitals?.gcs) || 15,
    },
    symptoms: Array.isArray(c.symptoms) ? c.symptoms.map(String) : [],
    correctTag: (['red', 'yellow', 'green', 'black'] as string[]).includes(c.correctTag) ? c.correctTag : 'yellow',
    timerDuration: Number(c.timerDuration) || 30,
    initialStatus: ['stable', 'deteriorating', 'critical'].includes(c.initialStatus) ? c.initialStatus : 'stable',
  }));
}
